import React, { useEffect, useMemo } from 'react';
import {
  estimateCredits,
  listModelsForModality,
  pickVideoModelSelection,
  rateForGeneratedAudio,
  defaultGenerateAudio,
  supportsGeneratedAudio,
  alwaysGeneratedAudio,
  supportsMultiImageReference,
  uiMaxReferenceImages,
  modelSupportsReferenceCount,
  modelSupportsDuration,
  planNeedsMultiClip,
  estimateClipPartCount,
  type VideoModelOption,
} from '../../lib/videoModelCatalog';
import SelectMenu from '../ui/SelectMenu';

export type VideoModelPickerValue = {
  videoModelId: string;
  resolutionId: string;
  durationSeconds: number;
  generateAudio?: boolean;
};

type Props = {
  modality: 't2v' | 'i2v' | 'v2v';
  value: VideoModelPickerValue;
  onChange: (next: VideoModelPickerValue) => void;
  durationSeconds: number;
  onDurationChange?: (seconds: number) => void;
  referenceDurationSeconds?: number;
  /** Lock to a single model (e.g. Kling recolor) */
  lockedModelId?: string;
  compact?: boolean;
  /** Most-used model for this product feature (community default) — shows crown */
  recommendedModelId?: string | null;
  /** Preferred order (best first). Used by the editor chat to rank by request complexity. */
  preferredModelIds?: string[];
  /** Caption under the crown. Defaults to the community-usage label. */
  recommendedHint?: string;
  /** Compact model switch for card headers. */
  variant?: 'full' | 'header';
  /** Hide the model list (e.g. when it already lives in the header). */
  hideModelSelect?: boolean;
  /** Hide models that cannot take this many reference / keyframe images. */
  minReferenceImages?: number;
  /** Hide models whose durationMax is below this (e.g. plan total seconds). */
  minDurationSeconds?: number;
  /** Raise the duration chip floor (e.g. template min on home). */
  durationFloorSeconds?: number;
  /**
   * When true, duration is fixed to `durationSeconds` (e.g. Fragmentador plan total,
   * or V2V edit that must keep the source clip length).
   * Hides the 5s/6s/… chips so debit and estimate cannot diverge.
   */
  lockDuration?: boolean;
  /** Hover text while the duration is locked (the script already chose the seconds). */
  lockDurationTitle?: string;
  /** Short label next to the locked seconds — default "roteiro"; V2V uses "vídeo original". */
  lockDurationHint?: string;
  /**
   * Fragmentador multi-clip: keep models that need N parts instead of hiding them.
   * Badge shows "N partes" when the plan exceeds a single job.
   */
  allowMultiClip?: boolean;
  /** Scene count used with allowMultiClip to compute part badges. */
  sceneCount?: number;
  /** Exact part count per model (real phase durations); overrides the uniform estimate. */
  partCount?: (modelId: string) => number;
  /** Exact debit for a pick (e.g. multi-part plans with stretched parts); overrides duration × rate. */
  quoteCredits?: (pick: VideoModelPickerValue) => number;
};

function durationOptions(model: VideoModelOption, floorSeconds?: number): number[] {
  const floor = Math.max(0, Math.round(Number(floorSeconds) || 0));
  const min = Math.max(1, model.durationMin || 4, floor);
  const max = Math.max(min, model.durationMax || 15);
  const opts: number[] = [];
  // Full integer range so planner totals (e.g. 16s) stay selectable as defaults.
  for (let s = min; s <= max; s += 1) opts.push(s);
  return opts.length ? opts : [min];
}

export default function VideoModelPicker({
  modality,
  value,
  onChange,
  durationSeconds,
  onDurationChange,
  referenceDurationSeconds,
  lockedModelId,
  compact,
  recommendedModelId,
  preferredModelIds,
  recommendedHint,
  variant = 'full',
  hideModelSelect = false,
  minReferenceImages,
  minDurationSeconds,
  durationFloorSeconds,
  lockDuration = false,
  lockDurationTitle,
  lockDurationHint = 'roteiro',
  allowMultiClip = false,
  sceneCount,
  partCount,
  quoteCredits,
}: Props) {
  const models = useMemo(() => {
    const all = listModelsForModality(modality);
    let filtered = lockedModelId ? all.filter((m) => m.id === lockedModelId) : all;
    // With multi-clip, keep models that can cover via N parts (still need ≥2 refs for i2v).
    if (!allowMultiClip) {
      if (minReferenceImages && minReferenceImages > 0) {
        filtered = filtered.filter((m) => modelSupportsReferenceCount(m, minReferenceImages));
      }
      if (minDurationSeconds && minDurationSeconds > 0) {
        filtered = filtered.filter((m) => modelSupportsDuration(m, minDurationSeconds));
      }
    } else {
      // Need room for identity start+end anchors plus at least one beat still.
      filtered = filtered.filter((m) => uiMaxReferenceImages(m) >= 3);
    }
    const order = [
      ...(preferredModelIds || []),
      ...(recommendedModelId ? [recommendedModelId] : []),
    ];
    if (!order.length) return filtered;
    const index = new Map(order.map((id, i) => [id, i]));
    return [...filtered].sort((a, b) => {
      const ia = index.has(a.id) ? (index.get(a.id) as number) : 500;
      const ib = index.has(b.id) ? (index.get(b.id) as number) : 500;
      if (ia !== ib) return ia - ib;
      if (a.id === recommendedModelId) return -1;
      if (b.id === recommendedModelId) return 1;
      return 0;
    });
  }, [
    modality,
    lockedModelId,
    recommendedModelId,
    preferredModelIds,
    minReferenceImages,
    minDurationSeconds,
    allowMultiClip,
  ]);

  const selection = useMemo(() => {
    try {
      return pickVideoModelSelection({
        videoModelId: lockedModelId || value.videoModelId,
        resolutionId: value.resolutionId,
        modality,
        forceKlingV2v: lockedModelId === 'kling-omni-v2v',
      });
    } catch {
      return pickVideoModelSelection({ modality });
    }
  }, [value.videoModelId, value.resolutionId, modality, lockedModelId]);

  const { model, resolution } = selection;
  const billedV2v = Boolean(model.billedOutputPlusRef);
  const refSec = referenceDurationSeconds ?? durationSeconds;
  const audioOn = Boolean(value.generateAudio) && supportsGeneratedAudio(model);
  const silentRate = rateForGeneratedAudio(resolution, false);
  const liveRate = rateForGeneratedAudio(resolution, audioOn);
  const audioRate = rateForGeneratedAudio(resolution, true);
  const quotePick = { ...value, videoModelId: model.id, resolutionId: resolution.id };
  const totalCredits = quoteCredits ? quoteCredits({ ...quotePick, generateAudio: audioOn }) : estimateCredits({
    creditsPerSecond: liveRate.creditsPerSecond,
    durationSeconds,
    referenceDurationSeconds: refSec,
    billedOutputPlusRef: billedV2v,
  });
  const silentCredits = quoteCredits ? quoteCredits({ ...quotePick, generateAudio: false }) : estimateCredits({
    creditsPerSecond: silentRate.creditsPerSecond,
    durationSeconds,
    referenceDurationSeconds: refSec,
    billedOutputPlusRef: billedV2v,
  });
  const audioCredits = quoteCredits ? quoteCredits({ ...quotePick, generateAudio: true }) : estimateCredits({
    creditsPerSecond: audioRate.creditsPerSecond,
    durationSeconds,
    referenceDurationSeconds: refSec,
    billedOutputPlusRef: billedV2v,
  });
  const canAudio = supportsGeneratedAudio(model);
  const audioAlwaysOn = alwaysGeneratedAudio(model);
  const sameAudioPrice = canAudio && audioCredits === silentCredits;

  const durations = durationOptions(model, durationFloorSeconds);
  const isRecommendedSelected = Boolean(recommendedModelId && model.id === recommendedModelId);

  const modelOptions = models.map((m) => {
    const crown = recommendedModelId && m.id === recommendedModelId ? '👑 ' : '';
    const premium = m.premium ? ' ★' : '';
    const maxRefs = uiMaxReferenceImages(m);
    const scenes = sceneCount && sceneCount > 0 ? sceneCount : minReferenceImages || 0;
    const dur = minDurationSeconds || durationSeconds || 0;
    let badge: string | undefined;
    if (allowMultiClip && scenes > 0 && (partCount || planNeedsMultiClip(m, scenes, dur))) {
      const parts = partCount ? partCount(m.id) : estimateClipPartCount(m, scenes, dur);
      badge = parts > 1 ? `${parts} partes` : undefined;
    }
    if (!badge && maxRefs > 2) {
      badge = `até ${maxRefs} cenas`;
    }
    return {
      value: m.id,
      label: `${crown}${m.label}${premium}`,
      badge,
    };
  });

  const floor = Math.max(0, Math.round(Number(durationFloorSeconds) || 0));

  const applyModelId = (nextId: string) => {
    const nextModel = models.find((m) => m.id === nextId) || model;
    const nextResolution = nextModel.resolutions[0] || model.resolutions[0];
    let nextDuration = durationSeconds;
    const minDur = Math.max(nextModel.durationMin || 1, floor);
    if (nextDuration < minDur) nextDuration = minDur;
    if (nextDuration > nextModel.durationMax) nextDuration = nextModel.durationMax;
    onChange({
      videoModelId: nextModel.id,
      resolutionId: nextResolution?.id || value.resolutionId,
      durationSeconds: nextDuration,
      generateAudio: defaultGenerateAudio(nextModel, nextResolution),
    });
    if (onDurationChange && nextDuration !== durationSeconds) onDurationChange(nextDuration);
  };

  // Keep selection at/above the template floor when it rises.
  useEffect(() => {
    if (lockDuration || !floor || !onDurationChange) return;
    if (durationSeconds >= floor) return;
    const next = Math.min(model.durationMax || floor, Math.max(floor, model.durationMin || floor));
    if (next === durationSeconds) return;
    onDurationChange(next);
    onChange({ ...value, durationSeconds: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floor, model.id, lockDuration]);

  // If filters remove the current selection, jump to the first compatible model.
  useEffect(() => {
    if (!models.length) return;
    if (models.some((m) => m.id === value.videoModelId)) return;
    const fallback = models[0];
    const nextResolution =
      fallback.resolutions.find((r) => r.id === value.resolutionId) || fallback.resolutions[0];
    let nextDuration = durationSeconds;
    const minDur = Math.max(fallback.durationMin || 1, floor);
    if (nextDuration < minDur) nextDuration = minDur;
    if (nextDuration > fallback.durationMax) nextDuration = fallback.durationMax;
    onChange({
      videoModelId: fallback.id,
      resolutionId: nextResolution?.id || value.resolutionId,
      durationSeconds: nextDuration,
      generateAudio: defaultGenerateAudio(fallback, nextResolution),
    });
    if (onDurationChange && nextDuration !== durationSeconds) onDurationChange(nextDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-pick when filter set / selection drifts
  }, [models, value.videoModelId]);

  if (variant === 'header') {
    return (
      <div style={{ marginLeft: 'auto', minWidth: 200, maxWidth: 280, flex: '1 1 200px' }}>
        <SelectMenu
          compact
          aria-label="Modelo de vídeo"
          value={model.id}
          options={modelOptions}
          onChange={applyModelId}
        />
      </div>
    );
  }

  const qualityOptions = model.resolutions.map((r) => {
    const rate = rateForGeneratedAudio(r, audioOn);
    return {
      value: r.id,
      label: `${r.label} — ${rate.creditsPerSecond} cr/s${audioOn && r.audio ? ' c/ som' : ''}`,
    };
  });

  const audioUnsupportedHint = (() => {
    if (alwaysGeneratedAudio(model)) {
      return 'Áudio nativo sempre incluso — a API do modelo não permite mudo.';
    }
    if (model.id === 'kling-omni-v2v') {
      return 'Kling Omni V2V não gera trilha nova (a API exige som gerado desligado). Use “manter áudio original” no clip.';
    }
    if (model.id.startsWith('poyo-wan27') || model.id === 'poyo-wan-edit') {
      return 'Wan 2.7 não gera trilha nativa. Use Wan 3.0 ou Seedance.';
    }
    return 'Este modelo não gera trilha nativa.';
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 8 : 10 }}>
      {!hideModelSelect && !lockedModelId && models.length > 1 && (
        <div>
          <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Modelo</div>
          <SelectMenu
            compact={compact}
            aria-label="Modelo de vídeo"
            value={model.id}
            options={modelOptions}
            onChange={applyModelId}
          />
          {recommendedModelId && (
            <div style={{ marginTop: 4, fontSize: '0.6rem', color: 'rgba(251,191,36,0.85)' }}>
              👑 {recommendedHint || 'Mais usado nesta função'}{isRecommendedSelected ? ' — default' : ''}
            </div>
          )}
        </div>
      )}

      {model.resolutions.length > 1 && (
        <div>
          <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Qualidade</div>
          <SelectMenu
            compact={compact}
            aria-label="Qualidade do vídeo"
            value={value.resolutionId}
            options={qualityOptions}
            onChange={(resolutionId) => onChange({ ...value, resolutionId, durationSeconds })}
          />
        </div>
      )}

      {lockDuration ? (
        <div>
          <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Duração</div>
          <div
            title={lockDurationTitle || `${durationSeconds}s (${lockDurationHint})`}
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: '0.75rem',
              background: 'rgba(255,255,255,0.04)',
              color: 'rgba(255,255,255,0.45)',
              border: '1px dashed rgba(255,255,255,0.16)',
              cursor: 'help',
              userSelect: 'none',
            }}
          >
            {durationSeconds}s <span style={{ color: 'rgba(255,255,255,0.35)' }}>({lockDurationHint})</span>
          </div>
        </div>
      ) : (
        onDurationChange && (
          <div>
            <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Duração</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {durations.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    onDurationChange(s);
                    onChange({ ...value, durationSeconds: s });
                  }}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 6,
                    border: 'none',
                    fontSize: '0.68rem',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    background: durationSeconds === s ? 'var(--primary, #6366f1)' : 'rgba(255,255,255,0.08)',
                    color: durationSeconds === s ? '#fff' : 'rgba(255,255,255,0.55)',
                  }}
                >
                  {s}s
                </button>
              ))}
            </div>
          </div>
        )
      )}

      <div>
        <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Som nativo</div>
        {audioAlwaysOn ? (
          <div
            style={{
              padding: compact ? '8px 10px' : '10px 12px',
              borderRadius: 8,
              background: 'linear-gradient(135deg, rgba(16,185,129,0.18), rgba(99,102,241,0.12))',
              border: '1px solid rgba(52,211,153,0.45)',
              color: '#ecfdf5',
            }}
            title="Este modelo sempre devolve áudio; a API não aceita mute"
          >
            <div style={{ fontSize: '0.72rem', fontWeight: 650, display: 'flex', alignItems: 'center', gap: 6 }}>
              Áudio sempre incluso
              <span
                style={{
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  padding: '1px 5px',
                  borderRadius: 999,
                  background: 'rgba(16,185,129,0.35)',
                  color: '#a7f3d0',
                }}
              >
                nativo
              </span>
            </div>
            <div style={{ fontSize: '0.6rem', marginTop: 4, color: 'rgba(167,243,208,0.75)', lineHeight: 1.4 }}>
              O modelo devolve trilha sincronizada; não há opção mudo na API. Preço já inclui o áudio (~{silentCredits}{' '}
              cr).
            </div>
          </div>
        ) : (
          <>
        <div
          role="group"
          aria-label="Som nativo"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 4,
            padding: 3,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.28)',
            border: '1px solid rgba(255,255,255,0.08)',
            opacity: canAudio ? 1 : 0.55,
          }}
        >
          {([
            { on: false, title: 'Mudo', credits: silentCredits },
            { on: true, title: 'Com som', credits: audioCredits },
          ] as const).map((opt) => {
            const selected = audioOn === opt.on;
            const disabled = opt.on && !canAudio;
            const freeAudioPerk = opt.on && sameAudioPrice;
            return (
              <button
                key={opt.title}
                type="button"
                disabled={disabled}
                title={
                  disabled
                    ? audioUnsupportedHint
                    : freeAudioPerk
                      ? 'Som nativo incluso — mesmo preço do mudo'
                      : undefined
                }
                onClick={() => onChange({ ...value, generateAudio: opt.on })}
                style={{
                  padding: compact ? '6px 8px' : '8px 10px',
                  borderRadius: 6,
                  border: freeAudioPerk
                    ? selected
                      ? '1px solid rgba(52,211,153,0.85)'
                      : '1px solid rgba(52,211,153,0.35)'
                    : selected
                      ? '1px solid rgba(129,140,248,0.7)'
                      : '1px solid transparent',
                  background: freeAudioPerk
                    ? selected
                      ? 'linear-gradient(135deg, rgba(16,185,129,0.42), rgba(99,102,241,0.28))'
                      : 'rgba(16,185,129,0.1)'
                    : selected
                      ? 'rgba(99,102,241,0.35)'
                      : 'transparent',
                  boxShadow: freeAudioPerk && selected
                    ? '0 0 0 1px rgba(52,211,153,0.25), 0 6px 16px rgba(16,185,129,0.18)'
                    : undefined,
                  color: freeAudioPerk
                    ? selected
                      ? '#ecfdf5'
                      : 'rgba(167,243,208,0.85)'
                    : selected
                      ? '#e0e7ff'
                      : 'rgba(255,255,255,0.55)',
                  fontFamily: 'inherit',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontSize: '0.7rem', fontWeight: 650, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {opt.title}
                  {freeAudioPerk && (
                    <span
                      style={{
                        fontSize: '0.55rem',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        padding: '1px 5px',
                        borderRadius: 999,
                        background: selected ? 'rgba(16,185,129,0.35)' : 'rgba(16,185,129,0.18)',
                        color: selected ? '#a7f3d0' : 'rgba(167,243,208,0.75)',
                      }}
                    >
                      incluso
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: '0.6rem',
                    marginTop: 2,
                    color: freeAudioPerk
                      ? selected
                        ? '#a7f3d0'
                        : 'rgba(167,243,208,0.65)'
                      : selected
                        ? '#c4b5fd'
                        : 'rgba(255,255,255,0.4)',
                  }}
                >
                  ~{opt.credits} cr
                  {freeAudioPerk ? ' · mesmo preço' : ''}
                  {opt.on && canAudio && audioCredits > silentCredits ? ` · +${audioCredits - silentCredits}` : ''}
                </div>
              </button>
            );
          })}
        </div>
        {!canAudio && (
          <div style={{ marginTop: 4, fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)' }}>
            {audioUnsupportedHint}
          </div>
        )}
          </>
        )}
      </div>

      <div
        style={{
          padding: compact ? '8px 10px' : '10px 12px',
          borderRadius: 8,
          background: 'rgba(99,102,241,0.08)',
          border: '1px solid rgba(99,102,241,0.2)',
          fontSize: compact ? '0.68rem' : '0.72rem',
          color: 'rgba(255,255,255,0.75)',
          lineHeight: 1.45,
        }}
      >
        <strong style={{ color: '#c4b5fd' }}>{liveRate.creditsPerSecond} cr/s</strong>
        {audioOn && liveRate.audioSurcharge ? ' com som' : audioOn ? ' (som incluso)' : ''}
        {' · '}
        Total estimado: <strong style={{ color: '#e9d5ff' }}>~{totalCredits} créditos</strong>
        {model.premium && (
          <span style={{ display: 'block', marginTop: 4, color: '#fbbf24', fontSize: '0.62rem' }}>
            Modelo premium — consome créditos mais rápido.
          </span>
        )}
        {model.billingNote && (
          <span style={{ display: 'block', marginTop: 4, color: 'rgba(255,255,255,0.45)', fontSize: '0.62rem' }}>
            {model.billingNote}
            {billedV2v && ` (~${totalCredits} cr com ref ≈ clip inteiro).`}
          </span>
        )}
        {supportsMultiImageReference(model) && (
          <span style={{ display: 'block', marginTop: 4, color: 'rgba(165,180,252,0.9)', fontSize: '0.62rem' }}>
            Aceita até {uiMaxReferenceImages(model)} cenas/referências · máx. {model.durationMax}s.
          </span>
        )}
      </div>
    </div>
  );
}

export function defaultPickerValue(modality: 't2v' | 'i2v' | 'v2v'): VideoModelPickerValue {
  const sel = pickVideoModelSelection({ modality });
  return {
    videoModelId: sel.videoModelId,
    resolutionId: sel.resolutionId,
    durationSeconds: modality === 'v2v' ? 5 : 5,
    generateAudio: defaultGenerateAudio(sel.model, sel.resolution),
  };
}
